import * as React from 'react';
import { useState, useEffect } from 'react';
import { injectIntl, InjectedIntlProps, defineMessages } from 'react-intl';
import { get as getDatabase, IDatabase, format, IDatabaseField, getAnalyticsToken } from '../../services/database';
import { get as getAttachment, IAttachment } from '../../services/attachment';
import { get as getPage, getOrganization, IPage } from '../../services/pages';
import { History } from 'history';
import Linkify from 'react-linkify';
import ReactGA from 'react-ga';

import './styles.css';
import logo from '../../images/logo-O-White.png';
import { IOrganization } from 'src/services/account';
import { string } from 'prop-types';
import headerBg from '../../images/water.jpg';
import pdf from '../../images/pdf.png';
import doc from '../../images/doc.png';
import eps from '../../images/eps.png';
import gif from '../../images/gif.png';
import jpg from '../../images/jpg.png';
import psd from '../../images/psd.png';
import xls from '../../images/xls.png';
import ColumnTemplate from './ColumnTemplate';
import { PropagateLoader } from 'react-spinners';

export default injectIntl(
    (
        props: InjectedIntlProps & {
            history: History;
            match: { params: { pageName?: string; databaseName?: string; accountName?: string } };
        }
    ) => {
        const [database, setDatabase] = useState<IDatabase | null>(null);
        const [page, setPage] = useState<IPage | null>(null);
        const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
        const [sections, setSections] = useState<Array<string | null>>([]);
        const [attachments, setAttachments] = useState<IAttachment[]>([]);
        const [organization, setOrganization] = useState<IOrganization | null>(null);

        const CONTACT_INFORMATION_SECTION = 'Contact Information';
        const GENERAL_INFORMATION_SECTION = 'General Information';
        const GENERAL_INFORMATION_LONG_DESCRIPTION = 'General_Information_long_description';
        const GENERAL_INFORMATION_ABOUT_US = 'General_Information_org_about_us';

        const messages = defineMessages({
            resourceDescription: {
                id: 'public.search.result.hdr.resourceDescription',
                defaultMessage: 'Resource Description',
            },
            returnToSearch: {
                id: 'public.search.result.hdr.returnToSearch',
                defaultMessage: 'Back',
            },
            attachments: {
                id: 'public.search.result.hdr.attachments',
                defaultMessage: 'Attachments',
            },
        });

        const addTracker = async () => {
            if (!database || !database.name || !page) return;
            try {
                const c = ((await getAnalyticsToken(database.name)) || '').replace(/"/g, '');
                ReactGA.addTrackers([
                    {
                        trackingId: c,
                        gaOptions: { name: 'client' },
                    },
                ]);
            } catch (ex) {
                console.error(ex);
            }
            const org = typeof page.organization === 'object' && page.organization;
            ReactGA.pageview(
                `${org && org.title}/${page.database && page.database.title}/${page.title}`,
                ['harmonize', 'client']
            );
        };

        useEffect(() => {
            addTracker();
        }, [page, database]);

        const loadDatabase = async (databaseName: string, accountName: string) => {
            const databaseResult = await getDatabase(databaseName, accountName);
            if (databaseResult.success && databaseResult.database) {
                const localSections: string[] = [];
                databaseResult.database.fields
                    .sort((a, b) => {
                        try {
                            const aMeta = JSON.parse(a.extra);
                            const bMeta = JSON.parse(a.extra);
                            return (
                                (parseInt(`${aMeta.sectionOrder}${aMeta.fieldOrder}`, 10) || 0) -
                                (parseInt(`${bMeta.sectionOrder}${bMeta.fieldOrder}`, 10) || 0)
                            );
                        } catch (ex) {
                            return 0;
                        }
                    })
                    .forEach((field, index) => {
                        if (
                            localSections.findIndex((x) => x === field.group) === -1 &&
                            field.group !== GENERAL_INFORMATION_SECTION
                        )
                            localSections.push(field.group);
                    });
                setSections(localSections?.length ? localSections : [GENERAL_INFORMATION_SECTION]);
                setDatabase(databaseResult.database);
            }
        };

        const loadPage = async (pageName: string, accountName: string) => {
            const pageResult = await getPage(pageName, accountName);
            if (pageResult.success && pageResult.pages) {
                const localPage = pageResult.pages[0];
                setPage(localPage);
                if (
                    localPage.database &&
                    typeof localPage.database === 'object' &&
                    localPage.database.name
                ) {
                    loadDatabase(localPage.database.name, accountName);
                }
                if (
                    localPage.organization &&
                    typeof localPage.organization === 'object' &&
                    localPage.organization.name
                ) {
                    loadOrganization(localPage.organization.name);
                }
                await fetchPageAttachments(localPage);
            }
        };

        const loadOrganization = async (orgName: string) => {
            const orgResult = await getOrganization(orgName);
            if (orgResult.success && orgResult.organization) {
                setOrganization(orgResult.organization);
            }
        };

        const fetchPageAttachments = async (currentPage: IPage) => {
            const att: IAttachment[] = [];
            if (Array.isArray(currentPage.row.map.attachments))
                await Promise.all(
                    currentPage.row.map.attachments.map(async (a: string) => {
                        const attachment = await getAttachment(
                            a,
                            typeof currentPage.organization === 'object'
                                ? currentPage.organization.name
                                : currentPage.organization
                        );
                        if (
                            attachment.success &&
                            attachment.data &&
                            !Array.isArray(attachment.data)
                        ) {
                            att.push(attachment.data);
                        }
                    })
                );
            setAttachments(att);
        };

        useEffect(() => {
            const { pageName, accountName } = props.match.params;
            if (pageName && accountName) loadPage(pageName, accountName);
        }, []);

        const renderTabs = () => {
            if (sections)
                return (
                    sections
                        // .filter(x => specialSections.indexOf(x || '') === -1)
                        .map((name, index) => {
                            return (
                                <li
                                    key={`${index}.${name}-tab`}
                                    className={`nav-item col text-center ${currentSectionIndex === index ? 'active' : ''
                                        }`}>
                                    <a
                                        className={`nav-link ${currentSectionIndex === index ? 'active' : ''
                                            }`}
                                        onClick={() => setCurrentSectionIndex(index)}
                                        id={`${index}-tab`}
                                        data-toggle="pill"
                                        href={`#${index}-tab`}
                                        role="tab"
                                        aria-selected="true">
                                        {name}
                                    </a>
                                </li>
                            );
                        })
                );
            return null;
        };

        const renderSectionLayout = () => {
            if (!page || !database) return null;
            return sections.map(section => {
                if (!section) return null;
                return (
                    <React.Fragment key={section}>
                        <div className="content-top">
                            <div className="row">
                                <div className="col-lg-12">
                                    <hr />
                                </div>
                            </div>
                            <h3 className="py-2">{section}</h3>
                            {Object.keys(page.row.map).map(fieldKey => {
                                const dbField = database.fields.find(field => format(section, field.name) === fieldKey);
                                if (dbField)
                                    return (
                                        <Linkify>
                                            <div className="my-1">
                                                <h5 className="pl-2 mb-2">{dbField.label}</h5>
                                                <p style={{ fontSize: 16, wordBreak: 'break-all' }} className="pl-4">{parseResult(page.row.map[fieldKey], dbField)}</p>
                                            </div>
                                        </Linkify>
                                    )

                                return null;
                            })}
                        </div>
                    </React.Fragment>
                )
            })
        };

    const renderList = (list: string[], databaseField: IDatabaseField) => {
        return <ul>{list.sort((a, b) => Array.isArray(databaseField.options) ? databaseField.options.indexOf(a) - databaseField.options.indexOf(b) : 0).map((item: string) => <li key={item}>{item}</li>)}</ul>;
    }

    const parseResult = (data: string, databaseField: IDatabaseField) => {
        const output = JSON.parse(data);
        if (typeof output === 'string') return output;
        else if (Array.isArray(output)) return renderList(output, databaseField);
        else return data;
    };

    // const renderContactSection = () => {
    //     if (sections.indexOf(CONTACT_INFORMATION_SECTION) !== -1 && page) {
    //         return (
    //             <div className="col-xs-4 col-lg-3 pt-5">
    //                 <h3>{CONTACT_INFORMATION_SECTION}</h3>
    //                 {
    //                     Object.keys(page.row.map).map(fieldKey => {
    //                         const section = fieldKey.split(':') && fieldKey.split(':').length === 2 ? fieldKey.split(':')[0] : null
    //                         const value = fieldKey.split(':') && fieldKey.split(':').length === 2 ? fieldKey.split(':')[1] : ''
    //                         if (section === CONTACT_INFORMATION_SECTION) {
    //                             return <p><strong>{value}</strong> <br />{parseResult(page.row.map[fieldKey])}</p>
    //                         }
    //                         return null
    //                     })
    //                 }
    //                 <div className="mapright">
    //                     <iframe
    //                         src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d886188.2409216223!2d-95.96174953501654!3d29.815995387800758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s211+Spaceway+Ave%2C+Houston+TX+29831!5e0!3m2!1sbn!2sbd!4v1547329023522"
    //                         width="100%"
    //                         height="250"
    //                         style={{ border: 'none' }}
    //                     />
    //                     <a href="#">Open Map</a>
    //                 </div>
    //             </div>
    //         );
    //     }
    //     return null;
    // }

    const renderSectionForCurrentTab = () => {
        if (page && database && sections) return Object.keys(page.row.map).map(fieldKey => {
            const dbField = database.fields.find(field => format(sections[currentSectionIndex] || '', field.name) === fieldKey);
            if (dbField) {
                const value = parseResult(page.row.map[fieldKey], dbField);
                return (
                    <Linkify>
                        <div className="my-4">
                            <p style={{ fontFamily: 'Open Sans', fontWeight: 'bold', fontSize: 16 }}>{dbField.label}</p>
                            <p style={{ fontSize: 16, fontWeight: 100 }}>{value}</p>
                        </div>
                    </Linkify>
                )
            }
            return null
        })
        return null
    };

        const getFileType = (name: string) => {
            const type = (name.split('.').pop() || '').toLocaleLowerCase();
            switch (type) {
                case 'pdf':
                    return pdf;
                case 'ppt':
                    return doc;
                case 'docx':
                    return doc;
                case 'doc':
                    return doc;
                case 'png':
                    return doc;
                case 'jpg':
                    return jpg;
                case 'jpeg':
                    return jpg;
                case 'eps':
                    return eps;
                case 'gif':
                    return gif;
                case 'psd':
                    return psd;
                case 'xls':
                    return xls;
                case 'xlsx':
                    return xls;
                case 'psd':
                    return psd;
                default:
                    return doc;
            }
        };


        const renderAttachments = () => {
            if (database && database.settings.allowAttachments && attachments.length)
                return (
                    <React.Fragment>
                        <div className="col-lg-12">
                            <hr />
                        </div>
                        <div className="col-lg-12 content-top">
                            <h3>{props.intl.formatMessage(messages.attachments)}</h3>
                            <ul className="pdflist">
                                {attachments.map(at => {
                                    return (
                                        <li key={`${new Date().getTime()}-${at.name}`} className='p-3 pl-5'>
                                            <a href={at.url} target="_blank">
                                                <img src={getFileType(at.fileName)} style={{ margin: 10 }} />
                                            </a>
                                            <span style={{ padding: 10 }}>
                                                {at.fileName}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </React.Fragment>
                );
            return null;
        }

        const renderDescription = () => {
            if (!page) return null;
            if (page.row.map[GENERAL_INFORMATION_LONG_DESCRIPTION])
                return (
                    <Linkify>
                        <p className="py-3" style={{ fontFamily: 'Open Sans', fontWeight: 600 }}>
                            {props.intl.formatMessage(messages.resourceDescription)}
                        </p>
                        <p
                            style={{ fontFamily: 'Open Sans', fontWeight: 200, fontSize: 16 }}
                            className="pb-4">
                            {JSON.parse(page.row.map[GENERAL_INFORMATION_LONG_DESCRIPTION])}
                        </p>
                    </Linkify>
                );
            return null;
        };

        const renderAboutUs = () => {
            console.log(page);
            if (!page) return null;
            return (
                <React.Fragment>
                    <h4 className="text-harmonize-blue font-weight-normal">
                        {' '}
                        {typeof page.organization === 'string'
                            ? page.organization
                            : page.organization.title}
                    </h4>
                    <p>
                        {page.row.map[GENERAL_INFORMATION_ABOUT_US]
                            ? JSON.parse(page.row.map[GENERAL_INFORMATION_ABOUT_US])
                            : ''}
                    </p>
                </React.Fragment>
            );
            // if (page.row.map[GENERAL_INFORMATION_ABOUT_US])
            //     return (<React.Fragment>
            //         <p>{JSON.parse(page.row.map[GENERAL_INFORMATION_ABOUT_US])}</p>
            //     </React.Fragment >
            //     );
        };

        const renderContent = () => {
            if (database && database.settings.layout === 'two')
                return (
                    <React.Fragment>
                        <span role="tablist">
                            <ul className="nav navpills row" id="pills-tab">
                                {renderTabs()}
                            </ul>
                        </span>
                        <div className="tab-content" id="pills-tabContent">
                            <div className="tab-pane fade show active" role="tabpanel">
                                <div className="py-3">
                                    {renderSectionForCurrentTab()}
                                    <div className="row">{renderAttachments()}</div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                );
            else if (database && database.settings.layout === "bigbuddy")
                return (
                    <React.Fragment>
                        <span role="tablist">
                            <ul className="nav navpills row" id="pills-tab">
                                {renderTabs()}
                            </ul>
                        </span>
                        <div className="tab-content" id="pills-tabContent">
                            <div className="tab-pane fade show active" role="tabpanel">
                                <div className="py-3">
                                    {renderSectionForCurrentTab()}
                                    <div className="row">
                                        {renderAttachments()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )
            else
                return (
                    <React.Fragment>
                        {renderSectionLayout()}
                        <div className="row">{renderAttachments()}</div>
                    </React.Fragment>
                );
        };

        const renderOrgAvatar = () => {
            if (organization && organization.avatar && organization.avatar.length > 0) {
                return <img src={organization.avatar} alt="" />;
            } else {
                return <img src={logo} className="avatar-placeholder" alt="" />;
            }
        };

        if (!page) return null;

        if (database && database.settings.layout === 'columns') {
            return <ColumnTemplate page={page} database={database} organization={organization} attachments={database && database.settings.allowAttachments ? attachments : null} />
        }

    if (!page) return null;

    if (database && database.settings.layout === 'columns') {
        return <ColumnTemplate page={page} database={database} organization={organization} attachments={database && database.settings.allowAttachments ? attachments : null} />
    }

    if (!database) return <PropagateLoader size={15} color="#29447b" />

    return (
        <section id={page.name} className="flex-fill infoPageContent">
            <header
                className="public container-fluid"
                style={{
                    backgroundImage: ` linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.65)), url(${headerBg})`,
                    minHeight: 200,
                    backgroundSize: 'cover',
                }}>
                <div className="op-wrapper container-fluid">
                    <button
                        className="btn btn-link text-white ml-4"
                        onClick={() => props.history.goBack()}>
                        <i className="fas fa-arrow-circle-left" />
                        &nbsp;{props.intl.formatMessage(messages.returnToSearch)}
                    </button>
                    <div className="navbar navbar-expand-lg navbar-dark">
                        <div className="col-sm-12 align-middle">
                            <h1
                                className="font-weight-light text-left text-white d-inline align-middle page-header"
                                style={{ fontFamily: 'Open Sans', fontWeight: 300 }}>
                                {!organization || !organization.website ? (
                                    page ? (
                                        typeof page.organization === 'string' ? (
                                            page.organization
                                        ) : (
                                            page.organization.title
                                        )
                                    ) : (
                                        ''
                                    )
                                ) : (
                                    <a
                                        href={`${organization.website.indexOf('http') === -1
                                            ? 'https://'
                                            : ''
                                            }${organization.website}`}
                                        className="text-white">
                                        {page
                                            ? typeof page.organization === 'string'
                                                ? page.organization
                                                : page.organization.title
                                            : ''}
                                    </a>
                                )}
                            </h1>
                        </div>
                    </div>
                </div>
            </header>
            <main role="main" id="content" tabIndex={-1}>
                <div className="d-flex flex-row flex-fill">
                    <div className="col pt-5 px-5">
                        <span className="harmonize-blue bold" style={{ fontSize: 24, fontFamily: 'Open Sans', fontWeight: 600 }}>{page.title}</span>
                        <div className="row">
                            <div className="col-lg-12">
                                {database && database.settings && database.settings.layout !== 'bigbuddy' && renderDescription()}
                            </div>
                            <div className="col-lg-12">
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </section>
    )
});



// WEBPACK FOOTER //
// ./src/public/pages/index.tsx